import React, { useState, useEffect, useCallback } from "react";
import api from "../../../const/axios";
import Question from "./../Package/Single/Question";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { withRouter } from "./../../../utils/withRouter";
import { loadingOn, loadingOff } from "./../../../redux/actions";
import { Helmet } from "react-helmet";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import ToggleAnswersButton from "../../Elements/ToggleAnswersButton";
import { usePageParam } from "@/hooks/usePageParam";

// ─── Minimal pagination ───────────────────────────────────────────────────────

interface PaginationProps {
  current: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
}

const SimplePagination: React.FC<PaginationProps> = ({ current, total, pageSize, onChange }) => {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - current) <= 2)
    .reduce<(number | "ellipsis")[]>((acc, p, idx, arr) => {
      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("ellipsis");
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className="flex items-center justify-end gap-2 mt-8">
      <button
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-blue-400 dark:hover:border-blue-900 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:hover:text-indigo-200"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      {pages.map((p, i) =>
        p === "ellipsis" ? (
          <span key={`e-${i}`} className="w-9 text-center text-gray-400 dark:text-gray-500 text-sm">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p as number)}
            className={cn(
              "w-9 h-9 rounded-lg text-sm font-medium border transition-colors",
              current === p
                ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-900 hover:text-blue-500 dark:hover:text-indigo-200"
            )}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onChange(current + 1)}
        disabled={current === totalPages}
        className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-blue-400 dark:hover:border-blue-900 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:hover:text-indigo-200"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

interface AuthorPageProps {
  params: { id: string };
  loadingOn: () => void;
  loadingOff: () => void;
}

const AuthorPage: React.FC<AuthorPageProps> = ({ params, loadingOn, loadingOff }) => {
  const [content, setContent] = useState<any[]>([]);
  const [user, setUser] = useState("");
  const [opens, setOpens] = useState<number[]>([]);
  const [openCommentQuestionId, setOpenCommentQuestionId] = useState<number | null>(null);
  const [count, setCount] = useState(0);

  // Page lives in the URL (?page=N) so it survives back/forward navigation
  const { page, goToPage } = usePageParam();

  const allOpen = (): boolean => {
    return content.every(co =>
      co.questions.every((q: any) => opens.includes(q.values[0].id))
    );
  };

  const toggleAll = () => {
    if (allOpen()) {
      setOpens([]);
    } else {
      const all: number[] = [];
      content.forEach(co => {
        co.questions.forEach((q: any) => all.push(q.values[0].id));
      });
      setOpens(all);
    }
  };

  const toggleOpen = (id: number) => {
    setOpens(prev =>
      prev.includes(id) ? prev.filter(o => o !== id) : [...prev, id]
    );
  };

  const toggleCommentSection = (questionId: number) => {
    setOpenCommentQuestionId((prev) => (prev === questionId ? null : questionId));
  };

  const fetchQuestions = useCallback(async (pg: number) => {
    loadingOn();
    try {
      const res = await api.get(
        `authors/questions?id=${params.id}&page=${pg}&perpage=30`
      );
      setContent(res.data.results);
      setUser(res.data.fullname);
      setCount(res.data.count);
    } finally {
      loadingOff();
    }
  }, [params.id, loadingOn, loadingOff]);

  useEffect(() => {
    fetchQuestions(page);
  }, [fetchQuestions, page]);

  const isAllOpen = allOpen();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>{user} | Müəllifin sualları — 3sual.az</title>
        <meta name="description" content={`${user} — sual ensiklopediyasında müəllifin sualları və mövzuları.`} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-left text-xl font-black text-gray-900 dark:text-gray-50">
            {user}{" "}
            {count > 0 && (
              <span className="text-base font-semibold text-gray-400 dark:text-gray-500">({count})</span>
            )}
          </h1>
          <ToggleAnswersButton isOpen={isAllOpen} onToggle={toggleAll} />
        </div>

        {/* Content */}
        {content.length > 0 ? (
          <div className="space-y-4">
            {content.map((co, qindex) => (
              <div
                key={qindex}
                className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5"
              >
                {/* Package name link */}
                <div className="mb-4 pb-3 border-b border-gray-100 dark:border-gray-800">
                  <Link
                    to={`/packages/${co.id}`}
                    className="text-lg font-bold text-blue-500 hover:underline dark:text-indigo-300"
                  >
                    {co.name} — {co.game}
                  </Link>
                </div>

                {co.questions.map((q: any, index: number) => (
                  <Question
                    key={index}
                    question={q}
                    index={index}
                    opens={opens}
                    toggleOpen={toggleOpen}
                    commentsOpen={openCommentQuestionId === q.values[0].id}
                    onToggleComments={() => toggleCommentSection(q.values[0].id)}
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-12 text-center">
            <p className="text-gray-400 dark:text-gray-500 font-medium">
              Bu müəllifin sualları əlavə edilməyib
            </p>
          </div>
        )}

        {/* Pagination */}
        <SimplePagination
          current={page}
          total={count}
          pageSize={30}
          onChange={goToPage}
        />
      </div>
    </div>
  );
};

const mapStateToProps = () => ({});

export default withRouter(
  connect(mapStateToProps, { loadingOff, loadingOn })(AuthorPage)
);
